import { accountKeys, type Capability, entryKeys, transferKeys } from '@taler/api-client'
import type { Money } from '@taler/domain'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/app/api'
import { useSession } from '@/app/session'
import { useToasts } from '@/app/toasts'
import { defineEntryExtension, type RegisteredExtension, type RowView } from '@/features/entries'
import CrossLedgerMovementRowDetail from '@/features/transfers/components/CrossLedgerMovementRowDetail.vue'

export interface UseCrossLedgerMovementExtensionOptions {
  readonly ledgerId: () => string | undefined
  readonly can: (capability: Capability) => boolean
}

export interface CrossLedgerMovementMatch {
  readonly key: string
  readonly label: string
  readonly kind: 'EXPENSE' | 'TRANSFER'
  readonly otherLedgerId: string
  /** Neither side is the active ledger - the backend's two-leg primitive has no third ledger to post from. */
  readonly bothForeign: boolean
}

export interface CrossLedgerMovementDraft {
  readonly targetAmount: Money | null
}

/** TRANSFER's two money accounts carry pinned currencies; a real difference needs its own typed amount. */
export function needsTargetAmount(view: RowView): boolean {
  const accountCurrency = view.account?.currency
  const counterCurrency = view.counters[0]?.currency
  return (
    view.kind === 'TRANSFER' &&
    accountCurrency !== undefined &&
    counterCurrency !== undefined &&
    accountCurrency !== counterCurrency
  )
}

/**
 * A row whose payment account (expense) or either money account (transfer) belongs to
 * another ledger: posts through the cross-ledger movement endpoints instead of the plain
 * batch (AGENTS.md's cross-ledger account picker). Both legs post immediately once the
 * picker already named the foreign account; the backend falls back to a pending proposal
 * only when the caller turns out not to hold posting rights there after all.
 */
export function useCrossLedgerMovementExtension(
  options: UseCrossLedgerMovementExtensionOptions,
): RegisteredExtension {
  const api = useApi()
  const session = useSession()
  const queryClient = useQueryClient()
  const toasts = useToasts()
  const { t } = useI18n()

  function ledgerName(ledgerId: string): string {
    return session.ledgers.value.find((ledger) => ledger.id === ledgerId)?.name ?? ledgerId
  }

  async function invalidate(ledgerAId: string, ledgerBId: string): Promise<void> {
    const keys = [
      ...transferKeys.invalidatedByWrite(ledgerAId, ledgerBId),
      ...accountKeys.invalidatedByWrite(ledgerAId),
      ...accountKeys.invalidatedByWrite(ledgerBId),
      ...entryKeys.invalidatedByWrite(ledgerAId),
      ...entryKeys.invalidatedByWrite(ledgerBId),
    ]
    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
  }

  return defineEntryExtension<CrossLedgerMovementMatch, CrossLedgerMovementDraft>({
    id: 'cross-ledger-movement',
    detect: (view) => {
      if (view.kind === 'INCOME' || !options.can('TRANSFER')) return undefined
      const accountLedgerId = view.row.accountLedgerId
      const counterLedgerId = view.row.splits[0]?.targetLedgerId
      if (accountLedgerId === undefined && counterLedgerId === undefined) return undefined
      const otherLedgerId = accountLedgerId ?? counterLedgerId!
      return {
        key: `${view.kind}:${otherLedgerId}`,
        label: t('transfers.extension.label', { ledger: ledgerName(otherLedgerId) }),
        kind: view.kind,
        otherLedgerId,
        bothForeign: accountLedgerId !== undefined && counterLedgerId !== undefined,
      }
    },
    carries: new Set(['payee', 'memo', 'tags']),
    initialDraft: () => ({ targetAmount: null }),
    problems: (match, draft, view) => {
      if (match.bothForeign) {
        return [{ severity: 'blocked', message: t('transfers.extension.bothForeignBlocked') }]
      }
      if (needsTargetAmount(view) && draft.targetAmount === null) {
        return [
          {
            severity: 'needs-details',
            message: t('transfers.extension.needsTargetAmount', { currency: view.counters[0]!.currency }),
          },
        ]
      }
      return []
    },
    detail: CrossLedgerMovementRowDetail,
    placement: 'inline',
    submit: {
      shape: 'command',
      run: async (match, draft, view) => {
        const activeLedgerId = options.ledgerId()!
        const memo = view.row.memo.trim()

        if (match.kind === 'EXPENSE') {
          const payerLedgerId = view.row.accountLedgerId!
          await api.transfers.crossLedgerExpenses(activeLedgerId, {
            expenseAccountId: view.row.splits[0]!.targetId!,
            expense: view.total!,
            payerLedgerId,
            payerAccountId: view.row.accountId!,
            payer: view.total!,
            date: view.row.date,
            ...(memo === '' ? {} : { memo }),
          })
          await invalidate(activeLedgerId, payerLedgerId)
          return draft
        }

        const sourceLedgerId = view.row.accountLedgerId ?? activeLedgerId
        const targetLedgerId = view.row.splits[0]?.targetLedgerId ?? activeLedgerId
        const transfer = await api.transfers.create(sourceLedgerId, {
          sourceAccountId: view.row.accountId!,
          source: view.total!,
          targetLedgerId,
          targetAccountId: view.row.splits[0]!.targetId!,
          target: needsTargetAmount(view) ? draft.targetAmount! : view.total!,
          date: view.row.date,
          ...(memo === '' ? {} : { memo }),
        })
        await invalidate(sourceLedgerId, targetLedgerId)
        if (transfer.status === 'PROPOSED') {
          toasts.push(
            'neutral',
            t('transfers.extension.proposedToast', { ledger: ledgerName(match.otherLedgerId) }),
          )
        }
        return draft
      },
    },
  })
}
